import {
  Box,
  Button,
  Flex,
  Input,
  SegmentGroup,
  SegmentGroupValueChangeDetails,
  Text,
} from '@chakra-ui/react';
import { memo, useCallback, useState } from 'react';
import { LuChevronRight, LuDollarSign, LuInfo, LuLandmark, LuZap } from 'react-icons/lu';

type TransferMode = 'deposit' | 'withdraw';

const MODES: TransferMode[] = ['deposit', 'withdraw'];

const MODE_LABELS: Record<TransferMode, string> = {
  deposit: 'Deposit',
  withdraw: 'Withdraw',
};

interface MockRow {
  currencyTitle: string;
  currencySubtitle: string;
  currencyRight: string | null;
  bankTitle: string;
  bankSubtitle: string;
  bankRight: string;
  bankRightSub: string | null;
  isInstant: boolean;
}

const MOCK_DATA: Record<TransferMode, MockRow> = {
  withdraw: {
    currencyTitle: 'Withdraw',
    currencySubtitle: 'United States Dollar',
    currencyRight: 'GHS 0.00',
    bankTitle: 'Transfer to',
    bankSubtitle: 'Checking ***8067',
    bankRight: 'Instant',
    bankRightSub: 'Fees apply',
    isInstant: true,
  },
  deposit: {
    currencyTitle: 'Deposit',
    currencySubtitle: 'United States Dollar',
    currencyRight: null,
    bankTitle: 'Transfer from',
    bankSubtitle: 'Checking ***9988',
    bankRight: 'GHS 199,971',
    bankRightSub: 'Limit',
    isInstant: false,
  },
};

const TransferCash = () => {
  const [mode, setMode] = useState<TransferMode>('deposit');
  const [amount, setAmount] = useState('');

  const data = MOCK_DATA[mode];

  const handleModeChange = useCallback((details: SegmentGroupValueChangeDetails) => {
    if (details.value != null) {
      setMode(details.value as TransferMode);
    }
  }, []);

  const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
  }, []);

  const handleMaxClick = useCallback(() => {
    setAmount('0.00');
  }, []);

  const onClickPreview = useCallback(() => {
    // no-op for mockup
  }, []);

  return (
    <Flex direction="column" p={5} gap={5} h="full">
      <SegmentGroup.Root
        value={mode}
        onValueChange={handleModeChange}
        size="xs"
        alignSelf="flex-start"
      >
        <SegmentGroup.Indicator bg="surface.accent" />
        {MODES.map((m) => (
          <SegmentGroup.Item key={m} value={m} cursor="pointer">
            <SegmentGroup.ItemText
              fontSize="sm"
              fontWeight="medium"
            >
              {MODE_LABELS[m]}
            </SegmentGroup.ItemText>
            <SegmentGroup.ItemHiddenInput />
          </SegmentGroup.Item>
        ))}
      </SegmentGroup.Root>
      <Flex alignItems="center" gap={2}>
        <Flex alignItems="baseline" flex={1} gap={2}>
          <Input
            variant="flushed"
            fontSize="5xl"
            fontWeight="bold"
            placeholder="0"
            value={amount}
            onChange={handleAmountChange}
            border="none"
            p={0}
            _focus={{ boxShadow: 'none' }}
            color="text.primary"
            type="text"
          />
          <Text fontSize="3xl" fontWeight="bold" color="text.placeholder">
            GHS
          </Text>
        </Flex>
        <Button
          variant="outline"
          size="xs"
          borderRadius="full"
          borderColor="border.light"
          onClick={handleMaxClick}
        >
          Max
        </Button>
      </Flex>
      <Flex direction="column" gap={0}>
        <Flex alignItems="center" gap={3} p={3} cursor="pointer">
          <Flex
            alignItems="center"
            justifyContent="center"
            boxSize="40px"
            borderRadius="full"
            bg="selection.100"
            flexShrink={0}
          >
            <LuDollarSign size={18} color="var(--chakra-colors-selection-500)" />
          </Flex>
          <Flex direction="column" flex={1}>
            <Text fontSize="sm" fontWeight="semibold" color="text.primary">
              {data.currencyTitle}
            </Text>
            <Text fontSize="xs" color="text.secondary">
              {data.currencySubtitle}
            </Text>
          </Flex>
          {data.currencyRight != null && (
            <Flex direction="column" alignItems="flex-end" mr={1}>
              <Text fontSize="xs" color="text.secondary" fontWeight="medium">
                {data.currencyRight}
              </Text>
              <Flex alignItems="center" gap={1}>
                <Text fontSize="xs" color="text.secondary">
                  Available
                </Text>
                <LuInfo size={12} color="var(--chakra-colors-text-secondary)" />
              </Flex>
            </Flex>
          )}
          <LuChevronRight size={18} color="var(--chakra-colors-text-secondary)" />
        </Flex>
        <Box w="2px" h="20px" bg="border.light" ml="31px" />
        <Flex alignItems="center" gap={3} p={3} cursor="pointer">
          <Flex
            alignItems="center"
            justifyContent="center"
            boxSize="40px"
            borderRadius="full"
            bg="selection.100"
            flexShrink={0}
          >
            <LuLandmark size={18} color="var(--chakra-colors-selection-500)" />
          </Flex>
          <Flex direction="column" flex={1}>
            <Text fontSize="sm" fontWeight="semibold" color="text.primary">
              {data.bankTitle}
            </Text>
            <Text fontSize="xs" color="text.secondary">
              {data.bankSubtitle}
            </Text>
          </Flex>
          <Flex direction="column" alignItems="flex-end" mr={1}>
            {data.isInstant ? (
              <>
                <Flex alignItems="center" gap={1}>
                  <LuZap size={12} color="var(--chakra-colors-green-500)" />
                  <Text fontSize="xs" color="green.500" fontWeight="medium">
                    {data.bankRight}
                  </Text>
                </Flex>
                {data.bankRightSub != null && (
                  <Text fontSize="xs" color="text.secondary">
                    {data.bankRightSub}
                  </Text>
                )}
              </>
            ) : (
              <>
                <Text fontSize="xs" color="text.secondary" fontWeight="medium">
                  {data.bankRight}
                </Text>
                {data.bankRightSub != null && (
                  <Text fontSize="xs" color="text.secondary">
                    {data.bankRightSub}
                  </Text>
                )}
              </>
            )}
          </Flex>
          <LuChevronRight size={18} color="var(--chakra-colors-text-secondary)" />
        </Flex>
      </Flex>
      <Button w="full" size="xs" borderRadius="md" onClick={onClickPreview}>
        Preview
      </Button>
    </Flex>
  );
};

export default memo(TransferCash);
